import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { mediaUrlFromMap } from "@/lib/api";

/**
 * Featured sponsor popup (Block 4 P0 rework).
 * A TRUE premium sponsor moment: centered on screen, large creative, subtle
 * blurred backdrop, elegant close — never a corner toast, never colliding
 * with the AI assistant (z-90 above its z-70; backdrop covers the screen).
 *
 * Trigger engine — each mode fires EXACTLY as configured:
 *   entry  -> shortly after the page settles (700ms)
 *   delay  -> after `delay_seconds`
 *   scroll -> when scroll depth reaches `scroll_pct` % of the page
 *             (falls back to a short delay when the page cannot scroll,
 *              so a configured popup is never silently unreachable)
 *
 * Frequency: once per session PER CONFIG — the session key embeds the
 * trigger settings, so organizers changing the config can re-verify it
 * without wiping their session. `?spopup=1` forces a preview (no marking).
 */
export const SponsorPopup = ({ data }) => {
  const { t } = useTranslation();
  const popup = data.sponsor_popup;
  const sponsors = data.sponsors || [];
  const featured = sponsors.find((s) => s.tier === "principal") || sponsors[0];
  const [show, setShow] = useState(false);
  const firedRef = useRef(false);

  const preview = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("spopup") === "1";

  useEffect(() => {
    if (!popup?.enabled || !featured) return undefined;
    const trigger = popup.trigger || "entry";
    const delaySeconds = popup.delay_seconds || 10;
    const scrollPct = popup.scroll_pct || 40;
    const key = `equus_sp_${data.event.id}_${trigger}_${delaySeconds}_${scrollPct}`;
    if (!preview && sessionStorage.getItem(key)) return undefined;

    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      if (!preview) sessionStorage.setItem(key, "1");
      setShow(true);
    };

    if (trigger === "scroll") {
      const el = document.scrollingElement || document.documentElement;
      const maxScroll = () => el.scrollHeight - window.innerHeight;
      // Unreachable percentage on a non-scrollable page -> honest fallback.
      if (maxScroll() < 48) {
        const timer = setTimeout(fire, 1500);
        return () => clearTimeout(timer);
      }
      const onScroll = () => {
        const pct = (el.scrollTop / Math.max(1, maxScroll())) * 100;
        if (pct >= scrollPct) {
          window.removeEventListener("scroll", onScroll);
          fire();
        }
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll(); // already past the threshold (e.g. anchor deep-link)
      return () => window.removeEventListener("scroll", onScroll);
    }
    const ms = trigger === "delay" ? delaySeconds * 1000 : 700;
    const timer = setTimeout(fire, ms);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popup?.enabled, popup?.trigger, popup?.delay_seconds, popup?.scroll_pct]);

  // Scroll-lock + ESC while open
  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setShow(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [show]);

  if (!popup?.enabled || !featured) return null;
  const banner = mediaUrlFromMap(data.media, featured.banner_media_id);
  const logo = mediaUrlFromMap(data.media, featured.logo_media_id);
  const creative = banner || logo;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-8"
          data-testid="sponsor-popup"
        >
          {/* Backdrop — subtle, click to dismiss */}
          <button
            type="button"
            aria-label={t("common.close")}
            onClick={() => setShow(false)}
            data-testid="sponsor-popup-backdrop"
            className="absolute inset-0 bg-[#0A0A0A]/45 backdrop-blur-md cursor-default"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 8 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-label={t("pub.sponsorPopupAria")}
            className="relative w-full max-w-xl md:max-w-2xl max-h-[88vh] overflow-y-auto rounded-[28px] bg-white shadow-[0_48px_140px_-24px_rgba(0,0,0,0.5)]"
          >
            <button
              type="button"
              onClick={() => setShow(false)}
              aria-label={t("common.close")}
              data-testid="sponsor-popup-close"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-black/5 flex items-center justify-center text-black/55 hover:text-black hover:bg-white shadow-sm transition-colors duration-200"
            >
              <X className="w-4.5 h-4.5 w-[18px] h-[18px]" aria-hidden="true" />
            </button>

            {/* Full sponsor creative */}
            {creative && (
              <a
                href={featured.url || undefined}
                target="_blank"
                rel="noopener noreferrer sponsored"
                className="block"
                data-testid="sponsor-popup-creative"
              >
                <img
                  src={creative}
                  alt={featured.name}
                  loading="lazy"
                  className={
                    banner
                      ? "w-full h-52 sm:h-64 md:h-72 object-cover"
                      : "w-full h-40 sm:h-48 object-contain bg-[#F7F7F5] p-8"
                  }
                />
              </a>
            )}

            <div className="p-7 sm:p-9 flex flex-col items-start gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#1D4D3B]">
                {t(`pub.tier_${featured.tier}`, t("pub.tier_principal"))}
              </span>
              <div className="flex items-center gap-4">
                {banner && logo && (
                  <img src={logo} alt="" className="h-10 max-w-[120px] object-contain" loading="lazy" />
                )}
                <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-[#0A0A0A]" data-testid="sponsor-popup-name">
                  {featured.name}
                </h2>
              </div>
              {popup.message && (
                <p className="text-sm text-[#737373] leading-relaxed max-w-prose" data-testid="sponsor-popup-message">
                  {popup.message}
                </p>
              )}
              <div className="flex items-center gap-4 pt-3">
                {featured.url && (
                  <a
                    href={featured.url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    data-testid="sponsor-popup-link"
                    className="inline-flex items-center gap-1.5 rounded-full h-11 px-7 bg-[#0A0A0A] text-white text-sm font-medium whitespace-nowrap hover:bg-black/80 transition-colors duration-300"
                  >
                    {t("pub.sponsorVisit")}
                    <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setShow(false)}
                  data-testid="sponsor-popup-dismiss"
                  className="text-sm text-[#737373] hover:text-[#0A0A0A] transition-colors duration-200"
                >
                  {t("pub.sponsorDismiss")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
